import { Controller } from 'stimulus';

class FocusController extends Controller {
  static targets = ['focusable'];

  initialize() {
    this.firstFocusable = this.focusableTargets[0];
    this.lastFocusable =
      this.focusableTargets[this.focusableTargets.length - 1];
    this.listener = this._listener.bind(this);
  }

  /**
   * Adds keydown listener to the window, and sets focus on `this.firstFocusable`
   *
   * @returns {void}
   */
  init() {
    window.addEventListener('keydown', this.listener, true);

    this.firstFocusable.focus();
    this.firstFocusable.closest('.pro-Modal')?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }

  /**
   * Removes keydown listener from the window.
   *
   * @param {Event} e click or keydown event
   * @returns {void}
   */
  cancel(e) {
    const focusCancelEvent = new CustomEvent(
      'focus-controller-blur',
      {
        detail: {
          triggerEvent: e,
        },
      }
    );

    window.removeEventListener('keydown', this.listener, true);
    window.dispatchEvent(focusCancelEvent);
  }

  /**
   * Listener to handle keydown event. Only continues if 'Tab' key is pressed.
   * If the last focusable element (`this.lastFocusable`) is currently active
   * (`document.activeElement`) then set focus on the first focusable element
   * (`this.firstFocusable`). If shiftKey is also pressed, work in reverse.
   * If esc key is pressed, invoke this.cancel() and remove listener.
   *
   * @param {Event} e keydown event
   * @returns {void}
   */
  _listener(e) {
    if (e.key === 'Escape') {
      this.cancel();
      return;
    }

    const isTabPressed = e.key === 'Tab' || e.keyCode === 9;

    if (!isTabPressed) {
      return;
    }

    if (e.shiftKey) {
      if (document.activeElement === this.firstFocusable) {
        this._focus(this.lastFocusable);
        e.preventDefault();
      }
    } else if (document.activeElement === this.lastFocusable) {
      this._focus(this.firstFocusable);
      e.preventDefault();
    }
  }

  /**
   * Given an element, scrollIntoView and apply focus to that element.
   *
   * @param {HTMLElement} elem focusable element to apply focus to
   * @returns {void}
   */
  _focus(elem) {
    elem.scrollIntoView({ behavior: 'smooth' });
    elem.focus();
  }
}

export default FocusController;
